import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import { useAdaptConnectorNaming } from '../../lib/adaptConnectorNaming'
import isAura from '../../lib/isAura'
import { CHARGEPOINT_MIN_CURRENT } from '../constants/ChargePointConstants'
import { ChargePoint, Connector, ConnectorPhaseRotations, EarthingSystem } from '../redux/api/web-api'
import { isChargingConnector } from '../../lib/findChargingConnector'
import { MAX_NAME_LENGTH } from '../constants/InputsLength'
import getInstallationPhases from '../../lib/charge-point/getInstallationPhases'

import useText from './useText'

export type ConnectorId = 1 | 2 | undefined
export interface IOcppFormInput {
    name: string | null | undefined
    description?: string | null
    nfcEnabled: boolean
    allowConnectCurrent: boolean
    earthingSystem: EarthingSystem | null | undefined
    callbackEnabledApiCustomerId: number | null
    supplyCurrent?: number
    installationPhases?: string
    connectorPhaseRotation: ConnectorPhaseRotations
    connectorType_1: string
    installationPhaseMode_1: string
    installationPhase_1: string
    ic_1: number
    uc_1: number
    lbc_1?: number
    lbOfflinePhase_1?: string
    connectorType_2?: string
    installationPhaseMode_2?: string
    installationPhase_2?: string
    ic_2?: number
    uc_2?: number
    lbc_2?: number
    lbOfflinePhase_2?: string
    externalId_1?: string
    externalId_2?: string
}

export const ocppChargerDefaultValues = (chargePoint: ChargePoint): IOcppFormInput => {
    let retVal: IOcppFormInput = {
        name: chargePoint.name,
        earthingSystem: chargePoint.earthingSystem,
        supplyCurrent: chargePoint.supplyCurrent,
        connectorPhaseRotation: chargePoint.connectorPhaseRotation,
        installationPhases: getInstallationPhases(chargePoint.connectors?.at(0)),
    }

    chargePoint.connectors?.forEach((connector) => {
        retVal = {
            ...retVal,
            [`installationPhaseMode_${connector.connectorId}`]: connector.installationPhaseMode,
            [`installationPhase_${connector.connectorId}`]: connector.installationPhase,
            [`ic_${connector.connectorId}`]: connector.installationCurrent,
            [`uc_${connector.connectorId}`]: connector.userCurrent,
            [`lbc_${connector.connectorId}`]: connector.loadBalancerOfflineCurrent,
            [`lbOfflinePhase_${connector.connectorId}`]: connector.loadBalancerOfflinePhase,
            [`externalId_${connector.connectorId}`]: connector.externalId,
        }
    })

    return retVal
}

const inputTransformation = (value: number | undefined) => {
    return !value || isNaN(value) ? 0 : value
}

const useSupplyCurrentValidationSchema = () => {
    const text = useText()

    return (factoryCurrent?: number | null) =>
        yup
            .number()
            .positive()
            .integer()
            .required(text('msgSupplyCurrentRequired'))
            .test('more-than-maxCurrent', function (value) {
                if (factoryCurrent && value && value > factoryCurrent) {
                    return this.createError({
                        path: this.path,
                        message: text('msgSupplyCurrentMoreThanMaxCurrent', {
                            factoryCurrent: factoryCurrent,
                        }),
                    })
                }
                return true
            })
            .min(
                CHARGEPOINT_MIN_CURRENT,
                text('msgValueCannotBeBelowChargerMinCurrent', { minCurrent: CHARGEPOINT_MIN_CURRENT })
            )
            .transform(inputTransformation)
}

const useInstallationCurrentValidationSchema = () => {
    const text = useText()

    return (chargePoint: ChargePoint, connectorId: number) =>
        yup
            .number()
            .positive()
            .integer()
            .required(text('msgInstallationCurrentRequired'))
            .min(
                chargePoint.connectors?.find((connector) => connector?.connectorId === connectorId)?.minCurrent ??
                    CHARGEPOINT_MIN_CURRENT,
                text('msgInstallationCurrentLimitLowerThanMin')
            )
            .test('less-than-maxCurrent', function (value, form) {
                const factoryCurrent = chargePoint.connectors?.find(
                    (connector) => connector?.connectorId === connectorId
                )?.factoryCurrent

                const maxCurrent = Math.min(form.parent.supplyCurrent, factoryCurrent ?? form.parent.supplyCurrent)
                if (value > maxCurrent) {
                    return this.createError({
                        path: this.path,
                        message: text('msgInstallationCurrentMoreThanConnectorMaxCurrent', {
                            maxCurrent: maxCurrent,
                        }),
                    })
                }
                return true
            })
            .test('more-than-user-current', text('msgInstallationCurrentLessThanUserCurrent'), function (value, form) {
                const ucVal = form.parent[`uc_${connectorId}`]
                if (!ucVal || isNaN(ucVal)) return true

                return ucVal <= value
            })
            .transform((value) => (isNaN(value) ? undefined : value))
}

const useLoadbalancerOfflineCurrentValidationSchema = () => {
    const text = useText()
    const adaptConnectorNaming = useAdaptConnectorNaming()

    return (chargePoint: ChargePoint, connectorId: number, connectorName: string, minCurrent: number) =>
        yup
            .number()
            .required(text('msgLBOfflineCurrent'))
            .min(
                chargePoint.connectors?.find((connector) => connector?.connectorId === connectorId)?.minCurrent ??
                    CHARGEPOINT_MIN_CURRENT,
                text('lblInstallationCurrentOfflineValidation', {
                    connectorType: adaptConnectorNaming(connectorName ?? ''),
                    minCurrent: minCurrent,
                })
            )
            .transform((value) => (isNaN(value) ? undefined : value))
            .test('less-than-installation-current', function (value, form) {
                if (form.parent[`ic_${connectorId}`] < value) {
                    return this.createError({
                        path: this.path,
                        message: text('lblInstallationCurrentOfflineValidationMax', {
                            connectorType: connectorName ? adaptConnectorNaming(connectorName) : '',
                            installationCurrent: form.parent[`ic_${connectorId}`],
                        }),
                    })
                }
                return true
            })
}

const useLoadbalancerOfflinePhaseSchema = () => {
    const text = useText()
    const adaptConnectorNaming = useAdaptConnectorNaming()

    return (connectorName: string | null | undefined) =>
        yup.string().required(
            text('lblLoadBalancerOfflinePhaseValidationConnector', {
                name: adaptConnectorNaming(connectorName ?? ''),
            })
        )
}

const useUserCurrentValidationSchema = () => {
    const text = useText()
    return (chargePoint: ChargePoint, connectorId: number) =>
        yup
            .number()
            .test('lower-than-min-not-zero', function (value) {
                const minValue =
                    chargePoint.connectors?.find((connector) => connector?.connectorId === connectorId)?.minCurrent ??
                    CHARGEPOINT_MIN_CURRENT
                if (value && value < minValue && value !== 0) {
                    return this.createError({
                        path: this.path,
                        message: text('msgUserCurrentLimitLowerThanMin'),
                    })
                }
                return true
            })
            .test('less-than-user-current', function (value, form) {
                const icVal = form.parent[`ic_${connectorId}`]
                if (value && icVal < value) {
                    return this.createError({
                        path: this.path,
                        message: text('msgUserCurrentMoreThanInstallationCurrent'),
                    })
                }
                return true
            })

            .transform((value) => (isNaN(value) ? undefined : value))
            .nullable()
            .optional()
}

export const useInstallationWizardValidationSchema = () => {
    const text = useText()
    const supplyCurrentValidationSchema = useSupplyCurrentValidationSchema()
    const userCurrentValidationSchema = useUserCurrentValidationSchema()
    const installationCurrentValidationSchema = useInstallationCurrentValidationSchema()
    const loadbalancerOfflineCurrentValidationSchema = useLoadbalancerOfflineCurrentValidationSchema()
    const loadbalancerOfflinePhaseValidationSchema = useLoadbalancerOfflinePhaseSchema()

    return (chargePoint: ChargePoint) => {
        const factoryCurrent =
            chargePoint &&
            chargePoint.connectors
                ?.filter(isChargingConnector)
                .reduce((acc: number, curr: Connector) => acc + Number(curr.factoryCurrent), 0)

        return yup
            .object({
                name: yup.string().required().max(MAX_NAME_LENGTH, text('lblNameError')),

                supplyCurrent: supplyCurrentValidationSchema(factoryCurrent),

                ic_1: installationCurrentValidationSchema(chargePoint, 1),
                uc_1: userCurrentValidationSchema(chargePoint, 1),
                ...(isAura(chargePoint) && {
                    ic_2: installationCurrentValidationSchema(chargePoint, 2),
                    uc_2: userCurrentValidationSchema(chargePoint, 2),
                }),
                ...(chargePoint.loadBalancerGroupId &&
                    chargePoint.connectors && {
                        lbc_1: loadbalancerOfflineCurrentValidationSchema(
                            chargePoint,
                            1,
                            chargePoint.connectors[0]?.name ?? '',
                            chargePoint.connectors[0]?.minCurrent ?? 0
                        ),
                        lbOfflinePhase_1: loadbalancerOfflinePhaseValidationSchema(chargePoint.connectors[0]?.name),
                        ...(isAura(chargePoint) && {
                            lbc_2: loadbalancerOfflineCurrentValidationSchema(
                                chargePoint,
                                2,
                                chargePoint.connectors[1]?.name ?? '',
                                chargePoint.connectors[1]?.minCurrent ?? 0
                            ),
                            lbOfflinePhase_2: loadbalancerOfflinePhaseValidationSchema(chargePoint.connectors[1]?.name),
                        }),
                    }),
            })
            .required()
    }
}

export const useOcppFormValidationSchema = () => {
    const supplyCurrentValidationSchema = useSupplyCurrentValidationSchema()
    const userCurrentValidationSchema = useUserCurrentValidationSchema()
    const installationCurrentValidationSchema = useInstallationCurrentValidationSchema()
    const loadbalancerOfflineCurrentValidationSchema = useLoadbalancerOfflineCurrentValidationSchema()
    const loadbalancerOfflinePhaseValidationSchema = useLoadbalancerOfflinePhaseSchema()
    const text = useText()

    return (chargePoint: ChargePoint) => {
        const factoryCurrent =
            chargePoint &&
            chargePoint.connectors
                ?.filter(isChargingConnector)
                .reduce((acc: number, curr: Connector) => acc + Number(curr.factoryCurrent), 0)

        return yup
            .object({
                name: yup.string().required().max(MAX_NAME_LENGTH, text('lblNameError')),

                supplyCurrent: supplyCurrentValidationSchema(factoryCurrent),

                ic_1: installationCurrentValidationSchema(chargePoint, 1),

                uc_1: userCurrentValidationSchema(chargePoint, 1),
                ...(isAura(chargePoint) && {
                    ic_2: installationCurrentValidationSchema(chargePoint, 2),
                    uc_2: userCurrentValidationSchema(chargePoint, 2),
                }),
                ...(chargePoint.loadBalancerGroupId &&
                    chargePoint.connectors && {
                        lbc_1: loadbalancerOfflineCurrentValidationSchema(
                            chargePoint,
                            1,
                            chargePoint.connectors[0]?.name ?? '',
                            chargePoint.connectors[0]?.minCurrent ?? 0
                        ),
                        lbOfflinePhase_1: loadbalancerOfflinePhaseValidationSchema(chargePoint.connectors[0]?.name),
                        ...(isAura(chargePoint) && {
                            lbc_2: loadbalancerOfflineCurrentValidationSchema(
                                chargePoint,
                                2,
                                chargePoint.connectors[1]?.name ?? '',
                                chargePoint.connectors[1]?.minCurrent ?? 0
                            ),
                            lbOfflinePhase_2: loadbalancerOfflinePhaseValidationSchema(chargePoint.connectors[1]?.name),
                        }),
                    }),
            })
            .required()
    }
}
const formConfig = {
    mode: 'onChange',
    reValidateMode: 'onChange',
    delayError: 500,
}

const useOcppFormValidation = (chargePoint: ChargePoint) => {
    const defaultValues = ocppChargerDefaultValues(chargePoint)
    const ocppFormValidationSchema = useOcppFormValidationSchema()

    return useForm<IOcppFormInput>({
        ...formConfig,
        defaultValues,
        resolver: yupResolver(ocppFormValidationSchema(chargePoint)),
    })
}

export const useOcppUserCurrentValidation = (chargePoint: ChargePoint) => {
    const userCurrentValidationSchema = useUserCurrentValidationSchema()
    const defaultValues = ocppChargerDefaultValues(chargePoint)
    const schema = yup
        .object({
            uc_1: userCurrentValidationSchema(chargePoint, 1),
            ...(isAura(chargePoint) && {
                uc_2: userCurrentValidationSchema(chargePoint, 2),
            }),
        })
        .required()

    return useForm<IOcppFormInput>({ ...formConfig, defaultValues, resolver: yupResolver(schema) })
}

export default useOcppFormValidation
